import { inject, Injectable } from '@angular/core';
import { LoadingController, ModalController } from '@ionic/angular';
import { TranslateService } from './translate.service';
import { Capacitor } from '@capacitor/core';
import { PassInformationPage } from 'src/app/pages/pass-information/pass-information.page';
import { AlertService, DispatcherService } from '.';
import { NativePluginService } from './plugin';
import { BaseJob, PassDetails, PassInformationPageResultEnum, Job } from '../types';

@Injectable({ providedIn: 'root' })
/** A service that gives shortcut functions to launch ionic alerts. */
export class PassService {
  private readonly alertSvc = inject(AlertService);
  private readonly dispatchSvc = inject(DispatcherService);
  private readonly gtm = (<any>window).dataLayer || [];
  private readonly loadingCtrl = inject(LoadingController);
  private readonly modalCtrl = inject(ModalController);
  private readonly pluginSvc = inject(NativePluginService);
  private readonly translateSvc = inject(TranslateService);

  private readonly PASS_STATUSES: Record<PassInformationPageResultEnum, PassResultsInfo> = {
    [PassInformationPageResultEnum.Sufficient]: {
      sufficientFunds: true,
      denied: false,
      invalid: false,
    },
    [PassInformationPageResultEnum.InsufficientProvide]: {
      sufficientFunds: false,
      denied: false,
      invalid: false,
    },
    [PassInformationPageResultEnum.ServerOfflineProvide]: {
      sufficientFunds: false,
      denied: false,
      invalid: false,
    },
    [PassInformationPageResultEnum.Deny]: { sufficientFunds: false, denied: true, invalid: false },
    [PassInformationPageResultEnum.Invalid]: {
      sufficientFunds: false,
      denied: false,
      invalid: true,
    },
  };

  async handleScanPass({
    ePassId,
    job,
  }: {
    ePassId: string;
    job: Job;
  }): Promise<PassResults | null> {
    let passId = ePassId;
    if (!passId) {
      passId = await this._scan();
    }
    if (!passId) {
      return null;
    }

    const loader = await this.getLoader({ isEPass: !!ePassId });
    await loader.present();
    const passInformation = await this.dispatchSvc.getPassInformation(passId, job.value);
    loader.dismiss();

    /** Pass is assigned to a different rider than the one assigned to the ride */
    const { assignedRiderName, isAssignedToRiderOnRide } = passInformation || {};
    if (assignedRiderName && !isAssignedToRiderOnRide) {
      const confirmed = await this.confirmPass({ assignedRiderName, riderName: job.riderName });
      if (!confirmed) {
        return null;
      }
    }
    const passResultInfo = await this.showPassInformationStatus({
      passInformation,
      job: job.value,
    });
    return {
      fareCollected: this.getFareCollected({ passInformation, job: job.value }),
      denied: !!passResultInfo.denied,
      passId,
      passInformation,
    };
  }

  //  #region Scan

  private async _scan(): Promise<string | null> {
    this.gtm.push({ event: 'custom_event', custom_event_name: 'click_ scan_pass' });
    // if we're not mobile (ie. in a sim), short circuit and return early.

    if (!Capacitor.isNativePlatform()) {
      return this._simScan();
    }

    const { cancelled, text } = await this.pluginSvc.activateBarcodeScanner(
      this.translateSvc.translate('LABEL.placeBarcode'),
    );
    return cancelled ? null : text;
  }

  private _simScan(): Promise<string | null> {
    return new Promise(resolve => {
      this.alertSvc.presentAlert({
        header: this.translateSvc.translate('LABEL.enterPassId'),
        inputs: [
          { type: 'text', name: 'passId', label: this.translateSvc.translate('LABEL.enterPassId') },
        ],
        buttons: [
          {
            text: this.translateSvc.translate('ACTION.cancel'),
            handler: () => resolve(null),
            cssClass: 'cancel ',
          },
          {
            text: this.translateSvc.translate('ACTION.ok').toUpperCase(),
            handler: async result => resolve(result?.passId),
            cssClass: 'primary ',
          },
        ],
      });
    });
  }

  //  #endregion

  private async getLoader({ isEPass }: { isEPass: boolean }): Promise<HTMLIonLoadingElement> {
    return this.loadingCtrl.create({
      spinner: 'crescent',
      message: `${this.translateSvc.translate('LABEL.checking')} ${isEPass ? 'ePass' : 'Pass'}...`,
    });
  }

  private confirmPass({
    assignedRiderName,
    riderName,
  }: {
    assignedRiderName: string;
    riderName: string;
  }): Promise<boolean> {
    return new Promise(resolve => {
      this.alertSvc.presentAlert({
        header: this.translateSvc.translate('LABEL.confirm'),
        message:
          `${this.translateSvc.translate('LABEL.passBelongs')} ${assignedRiderName} ${this.translateSvc.translate('LABEL.butPicking')} ` +
          `${this.translateSvc.translate('LABEL.up')} ${riderName}. ${this.translateSvc.translate('LABEL.continue')}?`,
        buttons: [
          {
            text: this.translateSvc.translate('LABEL.no'),
            handler: () => resolve(false),
            cssClass: 'danger ',
          },
          {
            text: this.translateSvc.translate('LABEL.yes'),
            handler: async () => resolve(true),
            cssClass: 'primary ',
          },
        ],
        cssClass: 'noscroll',
        backdropDismiss: false,
      });
    });
  }

  private async showPassInformationStatus({
    passInformation,
    job,
  }: {
    passInformation: PassDetails;
    job: BaseJob;
  }): Promise<PassResultsInfo> {
    const modal = await this.modalCtrl.create({
      component: PassInformationPage,
      cssClass: 'parascope-full-modal',
      componentProps: {
        passInformation: passInformation,
        job: job,
        showBackdrop: true,
        enableBackdropDismiss: false,
      },
    });

    // ePass
    if (passInformation?.isSufficientForRide) {
      return { sufficientFunds: true, denied: false, invalid: false };
    }

    await modal.present();
    const { data } = await modal.onDidDismiss();
    return this.PASS_STATUSES[data.status];
  }

  private getFareCollected({
    passInformation,
    job,
  }: {
    passInformation: PassDetails;
    job: BaseJob;
  }) {
    const { remainingBalance = 0 } = passInformation || { remainingBalance: 0 };
    const { rideFare = 0 } = job || { rideFare: 0 };
    if (!remainingBalance) return 0;
    if (remainingBalance < rideFare) return remainingBalance;
    return rideFare;
  }
}

export type PassResultsInfo = {
  sufficientFunds: boolean;
  denied: boolean;
  invalid: boolean;
};

export type PassResults = {
  fareCollected: number;
  denied: boolean;
  passId: string;
  passInformation: PassDetails;
};
